import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${props => props.theme.colors.white};
`;

const HeroSection = styled.section`
  width: 100%;
  height: 70vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${props => props.theme.colors.gradient};
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 1;
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.white};
  margin-bottom: 2rem;
  text-align: center;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.5rem;
  font-weight: 400;
  color: ${props => props.theme.colors.white};
  margin-bottom: 2rem;
  text-align: center;
  max-width: 700px;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 1.2rem;
  }
`;

const InfoSection = styled.section`
  padding: 5rem 0;
`;

const InfoContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 2rem;
  text-align: center;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const RideTypes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const RideCard = styled(motion.div)`
  padding: 2rem;
  border-radius: 1rem;
  box-shadow: ${props => props.theme.shadows.medium};
  background-color: ${props => props.theme.colors.white};
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-10px);
  }
`;

const RideIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const RideTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.dark};
  margin-bottom: 1rem;
`;

const RideDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.dark};
`;

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const RidesPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageContainer>
      <Navbar />
      <HeroSection>
        <ContentWrapper>
          <Title
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {t('rides.title', 'Book a Ride with Wassel')}
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {t('rides.subtitle', 'Fast, comfortable and affordable rides at your fingertips')}
          </Subtitle>
        </ContentWrapper>
      </HeroSection>

      <InfoSection>
        <InfoContainer>
          <SectionTitle>{t('rides.options', 'Ride Options')}</SectionTitle>
          <RideTypes>
            <RideCard
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <RideIcon>🚗</RideIcon>
              <RideTitle>{t('rides.economy', 'Economy')}</RideTitle>
              <RideDescription>
                {t('rides.economyDesc', 'Affordable rides for everyday travel. Perfect for commuting to work or running errands.')}
              </RideDescription>
            </RideCard>

            <RideCard
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <RideIcon>🚙</RideIcon>
              <RideTitle>{t('rides.comfort', 'Comfort')}</RideTitle>
              <RideDescription>
                {t('rides.comfortDesc', 'More spacious vehicles for a comfortable ride experience. Ideal for longer trips or small groups.')}
              </RideDescription>
            </RideCard>

            <RideCard
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <RideIcon>🚐</RideIcon>
              <RideTitle>{t('rides.premium', 'Premium')}</RideTitle>
              <RideDescription>
                {t('rides.premiumDesc', 'Luxury vehicles with top-rated drivers. Choose premium for special occasions or business travel.')}
              </RideDescription>
            </RideCard>
          </RideTypes>
        </InfoContainer>
      </InfoSection>

      <Footer />
    </PageContainer>
  );
};

export default RidesPage; 